import {
  Description,
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

type Props = {
  label: string
  placeholder: string
  options: string[]
  value: string
  error?: string
  onChange: (value: string) => void
}

export default function AuthSelect({ label, placeholder, options, value, error, onChange }: Props) {
  return (
    <Field>
      <Label className="mb-2 block text-[15px] font-medium text-stone-900">{label}</Label>

      <Listbox value={value} onChange={onChange} invalid={Boolean(error)}>
        <ListboxButton
          className={`group flex h-11 w-full items-center justify-between gap-2 rounded-lg border bg-white px-3.5 text-left text-base outline-none data-focus:ring-2 ${
            error
              ? 'border-red-400 data-focus:border-red-400 data-focus:ring-red-100'
              : 'border-stone-200 data-focus:border-purple-400 data-focus:ring-purple-100'
          }`}
        >
          <span className={value ? 'truncate text-stone-900' : 'truncate text-stone-400'}>
            {value || placeholder}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-stone-400 transition-transform group-data-open:rotate-180" />
        </ListboxButton>

        <ListboxOptions
          anchor={{ to: 'bottom start', gap: 6 }}
          transition
          className="z-50 max-h-[min(16rem,60vh)]! w-(--button-width) overflow-y-auto rounded-xl border border-stone-200 bg-white p-1 shadow-[0_2px_4px_rgba(28,25,23,0.04),0_18px_44px_-16px_rgba(28,25,23,0.22)] transition duration-100 ease-out focus:outline-none data-closed:scale-95 data-closed:opacity-0"
        >
          {options.map((option) => (
            <ListboxOption
              key={option}
              value={option}
              className="group flex h-11 cursor-pointer items-center justify-between rounded-lg px-3 text-[15px] text-stone-700 select-none data-focus:bg-stone-100 data-selected:text-purple-700 md:h-10"
            >
              <span className="truncate">{option}</span>
              <CheckIcon className="size-4 shrink-0 opacity-0 group-data-selected:opacity-100" />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>

      {error && <Description className="mt-1.5 block text-sm text-red-600">{error}</Description>}
    </Field>
  )
}
